import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../supabaseClient';
import { deleteItem } from '../../../api/items';
import { logAudit } from '../../../api/auditLog';
import { updateTicketActivity } from '../../../api/ticketActivity';
import { useConfirm } from '../../common/ConfirmDialog';
import './TicketTodoList.scss';

const TicketTodoList = forwardRef(({ ticketId, canEdit, onItemClick }, ref) => {
  const { profile, viewingCompanyId } = useAuth();
  const { confirm } = useConfirm();
  const [items, setItems] = useState([]);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadItems();
  }, [ticketId]);

  const loadItems = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('items')
      .select('*, creator_profiles:creator_id(id, email)')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true });

    if (!error) {
      setItems(data || []);
    }
    setLoading(false);
  };

  useImperativeHandle(ref, () => ({
    refresh: loadItems,
  }));

  // 排序：未完成在上 → 已完成按 completed_at DESC（最新完成的在最上）
  const sortedItems = useMemo(() => {
    const incomplete = items.filter((i) => !i.completed);
    const completed = items
      .filter((i) => i.completed)
      .sort((a, b) => {
        const dateA = a.completed_at ? new Date(a.completed_at) : new Date(0);
        const dateB = b.completed_at ? new Date(b.completed_at) : new Date(0);
        return dateB - dateA; // 最新完成的在前
      });
    return [...incomplete, ...completed];
  }, [items]);

  const handleAddItem = async () => {
    if (!newItemTitle.trim()) return;

    const { data, error } = await supabase
      .from('items')
      .insert({
        ticket_id: ticketId,
        title: newItemTitle.trim(),
        creator_id: profile.id,
        assignee_id: profile.id,
        status: 'pending',
        completed: false,
        display_order: items.length,
        department_id: profile.department_id,
        company_id: viewingCompanyId || profile.company_id,
      })
      .select('*, creator_profiles:creator_id(id, email)')
      .single();

    if (!error && data) {
      setItems((prev) => [...prev, data]);
      setNewItemTitle('');
      logAudit('create', 'item', data.id, data.title, {
        after: data,
      });
      updateTicketActivity(ticketId, profile.id);
    }
  };

  const handleToggleItem = async (item) => {
    const newCompleted = !item.completed;
    const { data, error } = await supabase
      .from('items')
      .update({
        completed: newCompleted,
        completed_at: newCompleted ? new Date().toISOString() : null,
        status: newCompleted ? 'completed' : 'pending',
      })
      .eq('id', item.id)
      .select('*, creator_profiles:creator_id(id, email)')
      .single();

    if (!error && data) {
      setItems((prev) => prev.map((i) => (i.id === data.id ? { ...data, creator_profiles: i.creator_profiles } : i)));
      logAudit('update', 'item', item.id, item.title, {
        before: { completed: item.completed, status: item.status },
        after: { completed: newCompleted, status: newCompleted ? 'completed' : 'pending' },
      });
      updateTicketActivity(ticketId, profile.id);
    }
  };

  const handleDeleteItem = async (itemId) => {
    const confirmed = await confirm({
      title: '刪除 ToDo',
      message: '確定要刪除這個 ToDo 項目嗎？',
      confirmText: '刪除',
      type: 'danger',
    });
    if (!confirmed) return;

    const { error } = await deleteItem(itemId);
    if (!error) {
      setItems((prev) => prev.filter((i) => i.id !== itemId));
    }
  };

  const completedCount = items.filter((i) => i.completed).length;
  const totalCount = items.length;

  return (
    <div className="ticket-todo-list">
      {/* Header with progress */}
      <div className="todo-header">
        <span className="todo-label">ToDo</span>
        {totalCount > 0 && (
          <span className="todo-count">{completedCount}/{totalCount}</span>
        )}
      </div>

      {/* Progress bar */}
      {totalCount > 0 && (
        <div className="todo-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(completedCount / totalCount) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Add new item */}
      {canEdit && (
        <div className="todo-add">
          <input
            type="text"
            placeholder="新增 ToDo..."
            value={newItemTitle}
            onChange={(e) => setNewItemTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddItem();
            }}
          />
          <button onClick={handleAddItem} disabled={!newItemTitle.trim()}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 2v10M2 7h10" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {/* Items */}
      {loading ? (
        <div className="todo-skeleton">
          {[1, 2].map((i) => (
            <div key={i} className="skeleton-todo-item">
              <div className="skeleton-checkbox" />
              <div className="skeleton-line w70 h14" />
            </div>
          ))}
        </div>
      ) : (
        <div className="todo-items">
          {sortedItems.map((item) => {
            const isOwner = item.creator_id === profile?.id;
            return (
            <div key={item.id} className={`todo-item ${item.completed ? 'completed' : ''}`}>
              <div
                className={`todo-checkbox ${item.completed ? 'checked' : ''} ${!isOwner ? 'disabled' : ''}`}
                onClick={() => isOwner && handleToggleItem(item)}
              />
              <span
                className={`todo-title ${onItemClick ? 'clickable' : ''}`}
                onClick={() => onItemClick?.(item)}
                title={onItemClick ? '點擊查看詳情' : ''}
              >
                {item.title}
              </span>
              {isOwner && (
                <button
                  className="todo-delete"
                  onClick={() => handleDeleteItem(item.id)}
                  title="刪除"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M2 2l8 8M10 2l-8 8" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default TicketTodoList;
